import useUserPreference from './useUserPreference';

// Deriva la lista de campos ocultables/reordenables a partir de las columnas:
// excluye selección, acciones, las marcadas fixed y las que no tienen cabecera
// de texto (estructurales).
export const fieldsFromColumns = (columns) => columns
  .filter((c) => !c.fixed && c.key !== 'select' && c.key !== 'actions' && typeof c.header === 'string' && c.header !== '')
  .map((c) => ({ key: c.key, label: c.header }));

const asArr = (v) => (Array.isArray(v) ? v : []);

// Orden + visibilidad de un listado, INDEPENDIENTES por modalidad: la vista
// tabla y la vista tarjetas tienen su propia selección de campos y su propio
// orden. Persistido por usuario + navegador + pantalla en BD (useUserPreference):
//   <scope>:tableFields = { order: [keys...], hidden: [keys...] }
//   <scope>:cardFields  = { order: [keys...], hidden: [keys...] }
//
// Migración suave: si una modalidad aún no tiene preferencia propia, hereda los
// campos ocultos de la antigua clave unificada <scope>:hiddenCols (no se pierde
// lo ya configurado) y el orden natural de las columnas.
//
// La API opera SIEMPRE sobre la modalidad ACTIVA (la de viewMode), que es justo
// la vista que se está pintando (la tabla solo se monta en 'table' y las
// tarjetas solo en 'cards'), de modo que cada pantalla cambia lo mínimo.
//
// Devuelve:
//   hidden            : array de keys ocultas en la modalidad activa
//   isHidden(key)     : boolean
//   fields(columns)   : [{key,label}] ocultables, en el orden de la modalidad
//   toggle(key)       : alterna la visibilidad de un campo
//   reorder(keys)     : fija el nuevo orden (array de keys en el orden deseado)
//   orderColumns(cols): columnas reordenadas según la preferencia (las
//                       estructurales se quedan en su sitio); para DataTable
//   cardStyle(key)    : { order } para la fila de tarjeta (el body es flex col)
const useFieldLayout = (scope, viewMode) => {
  const mode = viewMode === 'table' ? 'table' : 'cards';
  const [legacyHidden] = useUserPreference(`${scope}:hiddenCols`, []);
  const [stored, setStored] = useUserPreference(`${scope}:${mode}Fields`, null);

  const hidden = stored && Array.isArray(stored.hidden) ? stored.hidden : asArr(legacyHidden);
  const savedOrder = stored && Array.isArray(stored.order) ? stored.order : [];

  const isHidden = (key) => hidden.includes(key);

  // Orden completo y saneado para un conjunto de keys: respeta el guardado,
  // descarta las que ya no existen y añade al final las nuevas.
  const fullOrder = (keys) => [
    ...savedOrder.filter((k) => keys.includes(k)),
    ...keys.filter((k) => !savedOrder.includes(k)),
  ];

  const fields = (columns) => {
    const all = fieldsFromColumns(columns);
    const full = fullOrder(all.map((f) => f.key));
    return [...all].sort((a, b) => full.indexOf(a.key) - full.indexOf(b.key));
  };

  const toggle = (key) => setStored((prev) => {
    const curHidden = prev && Array.isArray(prev.hidden) ? prev.hidden : asArr(legacyHidden);
    const curOrder = prev && Array.isArray(prev.order) ? prev.order : savedOrder;
    const set = new Set(curHidden);
    if (set.has(key)) set.delete(key); else set.add(key);
    return { order: curOrder, hidden: Array.from(set) };
  });

  const reorder = (nextOrderKeys) => setStored((prev) => ({
    order: nextOrderKeys,
    hidden: prev && Array.isArray(prev.hidden) ? prev.hidden : asArr(legacyHidden),
  }));

  const orderColumns = (columns) => {
    const all = fieldsFromColumns(columns);
    const full = fullOrder(all.map((f) => f.key));
    const movable = new Set(all.map((f) => f.key));
    const ordered = columns
      .filter((c) => movable.has(c.key))
      .sort((a, b) => full.indexOf(a.key) - full.indexOf(b.key));
    let i = 0;
    return columns.map((c) => (movable.has(c.key) ? ordered[i++] : c));
  };

  // Posición de la fila en la tarjeta. Sin orden guardado, todas comparten el
  // mismo valor y se respeta el orden natural del JSX (flex 'order' iguales).
  const cardStyle = (key) => {
    const i = savedOrder.indexOf(key);
    return { order: i === -1 ? 999 : i };
  };

  return { hidden, isHidden, fields, toggle, reorder, orderColumns, cardStyle };
};

export default useFieldLayout;
